import { useState, useCallback, useEffect, useRef } from 'react';
import ReactTooltip from 'react-tooltip';
import debounce from 'lodash/debounce';
import cloneDeep from 'lodash/cloneDeep';
import flow from 'lodash/flow';

import {
  saveColumnStateToLocalStorage,
  loadColumnStateFromStorage,
} from './utils/gridStorageProxy';
import { eventSources } from './constants';

import onSelectionChangedCallback from './callbacks/onSelectionChanged';
import { mergeValidationMessages } from './useEditableGridLogic.helpers';

export const GridMode = {
  View: 'View',
  EditAll: 'EditAll',
  Add: 'Add',
  Edit: 'Edit',
};

export const rowMetaData = {
  isUpdated: false,
  isAdded: false,
  validationMessages: {},
  touched: {},
  forceShowErrors: false,
  sortToTop: false,
  lastAsyncValidationMessages: {},
};

export const isEditingMode = mode => [GridMode.Add, GridMode.Edit, GridMode.EditAll].includes(mode);

const initializeRowData = data => data.map(
  row => ({
    ...row,
    meta: {
      ...rowMetaData,
      ...row.meta,
    },
  })
);

const initializeRowDataWithIds = (data, idField) => {
  // Defensive check to prevent processing too much data
  if (!Array.isArray(data)) {
    return [];
  }

  const clonedData = initializeRowData(data);
  const usedIds = new Set(); // Track used IDs to prevent duplicates
  const processedData = [];

  clonedData.forEach((row, index) => {
    // Skip if row is null, undefined, or not an object
    if (!row || typeof row !== 'object') {
      return;
    }

    // Ensure row has meta object
    if (!row.meta) {
      row.meta = { ...rowMetaData };
    }

    const hasValidId = row[idField] !== null && row[idField] !== '' && typeof row[idField] !== 'undefined';

    // If the row has a valid ID, check for duplicates
    if (hasValidId) {
      if (usedIds.has(row[idField])) {
        return; // Skip duplicate
      }
      usedIds.add(row[idField]);
    } else {
      // Generate unique fallback IDs
      let newId;
      if (row.meta?.isAdded) {
        newId = `temp-${index}`;
        // Ensure uniqueness
        let counter = 0;
        while (usedIds.has(newId)) {
          newId = `temp-${index}-${counter}`;
          counter++;
        }
        row.meta.tempId = newId;
        delete row.meta.fallbackId;
      } else {
        newId = `fallback-${index}`;
        // Ensure uniqueness
        let counter = 0;
        while (usedIds.has(newId)) {
          newId = `fallback-${index}-${counter}`;
          counter++;
        }
        row.meta.fallbackId = newId;
        delete row.meta.tempId;
      }
      usedIds.add(newId);
    }

    processedData.push(row);
  });

  return processedData;
};

const isCurrentValidation = (rowNode, validationPromise) =>
  rowNode && validationPromise === rowNode.data.meta.pendingValidation;

const rebuildReactTooltip = debounce(ReactTooltip.rebuild, 200);

const addNullFilterForExistingFilters = filters =>
  Object.keys(filters).reduce((newFilters, filterKey) => {
    newFilters[filterKey] = filters[filterKey];
    newFilters[filterKey].values = [null, ...newFilters[filterKey].values];
    return newFilters;
  }, {});

export const useEditableGridLogic = ({
  data,
  onReset,
  onSave,
  onCancel,
  onSelect,
  idField,
  getRowUniqueId,
  mode,
  createRow,
  context,
  validateRow,
  asyncValidateRow,
  clientId,
  includeMetaWhenSaving,
  onColumnEverythingChanged: onCustomColumnEverythingChanged,
}) => {
  const [gridApi, setGridApi] = useState(null);
  const [arePendingValidations, setArePendingValidations] = useState(false);
  const [editedRowId, setEditedRowId] = useState(null);
  const [dataHasErrors, setDataHasErrors] = useState(false);

  const filtersRef = useRef({ original: {}, custom: {} });
  const columnStateRestored = useRef({ restored: false });
  useEffect(() => rebuildReactTooltip(), [data]);

  const onGridReady = useCallback(({ api }) => {
    api.addEventListener('bodyScroll', rebuildReactTooltip);
    setGridApi(api);
  }, [setGridApi]);

  const onColumnEverythingChanged = useCallback(({ source }) => {
    if (columnStateRestored.current.restored) {
      columnStateRestored.current.restored = false;
    } else if (gridApi && source !== eventSources.TOOL_PANEL_UI) {
      loadColumnStateFromStorage(gridApi, clientId);
      columnStateRestored.current.restored = true;
    }
    onCustomColumnEverythingChanged?.();
  }, [clientId, columnStateRestored, gridApi, onCustomColumnEverythingChanged]);

  const findRowIndexBy = useCallback((data, comparator) => {
    let index = -1;
    gridApi.forEachNode((node, currentIndex) => {
      if (comparator(node.data, data)) {
        index = currentIndex;
      }
    });
    return index;
  }, [gridApi]);

  const clearRangeSelection = useCallback(() => {
    gridApi.clearRangeSelection();
    gridApi.clearFocusedCell();
  }, [gridApi]);

  const getRowNodeId = useCallback(
    rowData => {
      // Handle case where AG Grid v30 passes a row node instead of row data
      let actualData = rowData;
      if (rowData && typeof rowData === 'object' && rowData.data && !rowData[idField] && !rowData.meta) {
        // AG Grid v30 behavior - extract data from row node object
        actualData = rowData.data;
      }

      // Validate that we have actual row data
      if (!actualData || typeof actualData !== 'object') {
        return `invalid-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
      }

      // First priority: custom row ID function
      if (getRowUniqueId) {
        try {
          const customId = getRowUniqueId(actualData);
          if (customId !== null && customId !== '' && typeof customId !== 'undefined') {
            return String(customId);
          }
        } catch (error) {
          // Failed to getRowNodeId validation result - silently continue to prevent console warnings
        }
      }

      // Second priority: explicit ID field value (exceptionId for this grid)
      const id = actualData[idField];
      if (typeof id !== 'undefined' && id !== null && id !== '') {
        return String(id);
      }

      // Third priority: existing meta IDs (should always exist after initializeRowDataWithIds)
      if (actualData.meta?.tempId) {
        return String(actualData.meta.tempId);
      }
      if (actualData.meta?.fallbackId) {
        return String(actualData.meta.fallbackId);
      }

      const emergencyId = `emergency-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
      return String(emergencyId);
    },
    [getRowUniqueId, idField]
  );

  const processCellFromClipboard = useCallback(({ column, value, node }) => {
    const { valueParser } = column.colDef;
    const oldValue = node.data[column.colDef.field];
    return valueParser ? valueParser({ newValue: value, oldValue }) : value;
  }, []);

  const refreshRows = useCallback(({ rowNodes, refreshClientSideRowModel } = {}) => {
    if (gridApi) {
      gridApi.refreshCells({ rowNodes, force: true });
      rebuildReactTooltip();
      if (refreshClientSideRowModel) {
        gridApi.refreshClientSideRowModel();
      }
    }
  }, [gridApi]);

  useEffect(() => {
    refreshRows();
    if (gridApi) {
      gridApi.refreshHeader();
    }
  }, [gridApi, mode, refreshRows]);

  const getOriginalFilters = useCallback(
    () => filtersRef.current.original
    , [filtersRef]);
  const getCustomFilters = useCallback(
    () => filtersRef.current.custom
    , [filtersRef]);

  const applyFilters = useCallback(() => {
    const customFilters = getCustomFilters();
    const originalFilters = getOriginalFilters();
    const mergedFilters = Object.keys(originalFilters).reduce((filterAcc, key) => {
      if (originalFilters[key] && customFilters[key]) {
        filterAcc[key].values.push(...customFilters[key].values);
      }
      return filterAcc;
    }, cloneDeep(originalFilters));
    gridApi.setFilterModel(mergedFilters);
  }, [gridApi, getOriginalFilters, getCustomFilters]);

  const updateCustomFiltersWithRow = useCallback(rowData => {
    const originalFilters = getOriginalFilters();
    const newCustomFilters = Object.keys(originalFilters).reduce((filterAcc, filterKey) => {
      const [columnName] = filterKey.split('_');
      const valueFromRow = rowData[columnName];
      const originalFiltersContainValue = originalFilters[filterKey].values.includes(valueFromRow);
      const accForKeyExistsAndContainsValue = filterAcc[filterKey]
        && filterAcc[filterKey].values.includes(valueFromRow);
      if (!originalFiltersContainValue && !accForKeyExistsAndContainsValue) {
        filterAcc[filterKey] = cloneDeep(originalFilters[filterKey]);
        filterAcc[filterKey].values = [valueFromRow || null];
      }
      return filterAcc;
    }, {});
    filtersRef.current.custom = newCustomFilters;
  }, [filtersRef, getOriginalFilters]);

  const addBlanksToFilters = useCallback(() => {
    const filtersWithNulls = flow([
      getOriginalFilters,
      cloneDeep,
      addNullFilterForExistingFilters,
    ])();
    gridApi.setFilterModel(filtersWithNulls);
  }, [gridApi, getOriginalFilters]);

  const extractOriginalFilters = useCallback(() => {
    const currentFilters = gridApi.getFilterModel();
    return Object.keys(currentFilters).reduce((accFilters, key) => {
      const customFilter = getCustomFilters()[key];
      accFilters[key] = cloneDeep(currentFilters[key]);
      if (customFilter) {
        accFilters[key].values = accFilters[key].values.filter(val => val !== customFilter.values[0]);
      }
      return accFilters;
    }, {});
  }, [gridApi, getCustomFilters]);

  const onFilterChanged = useCallback(({ afterDataChange }) => {
    if (afterDataChange === false) {
      const newOriginalFilters = extractOriginalFilters();
      filtersRef.current.original = newOriginalFilters;
    }
  }, [filtersRef, extractOriginalFilters]);

  const onColumnVisible = useCallback(({ source }) => {
    if (source === eventSources.TOOL_PANEL_UI || source === eventSources.COLUMN_MENU) {
      saveColumnStateToLocalStorage(gridApi, clientId);
    }
  }, [clientId, gridApi]);

  useEffect(() => {
    if (gridApi && Array.isArray(data)) {
      // Process data with defensive validation
      const clonedData = initializeRowDataWithIds(data, idField);

      gridApi.setGridOption('rowData', clonedData);
    } else if (gridApi && !Array.isArray(data)) {
      gridApi.setGridOption('rowData', []);
    }
  }, [gridApi, data, idField]);

  const shouldBeSortedToTop = ({ validationMessages, isAdded }) =>
    (Object.keys(validationMessages?.errors ?? {}).length > 0) || isAdded;

  const reset = useCallback(() => {
    if (!gridApi) {
      return;
    }

    // Stop all editing and clear grid state completely
    gridApi.stopEditing(true);
    gridApi.deselectAll();
    gridApi.clearFocusedCell();

    // Clear any existing filters that might cause data duplication
    gridApi.setFilterModel(null);

    // Clear the row data completely first to force AG Grid to clean up
    gridApi.setGridOption('rowData', []);

    // Force garbage collection of nodes by triggering a refresh
    gridApi.refreshClientSideRowModel('everything');

    // Small delay to ensure AG Grid has processed the empty state
    setTimeout(() => {
      if (!gridApi) {
        return;
      }

      // Validate that we have data to work with
      if (!Array.isArray(data)) {
        gridApi.setGridOption('rowData', []);
        setDataHasErrors(false);
        onReset?.();
        return;
      }

      // Create fresh data with simple IDs
      const clonedData = initializeRowDataWithIds(data, idField);

      // Set the new data
      gridApi.setGridOption('rowData', clonedData);
      refreshRows();

      // Reset error state
      setDataHasErrors(false);
      onReset?.();
    }, 0);
  }, [gridApi, data, onReset, refreshRows, idField]);

  const save = useCallback(async () => {
    const updatedRows = [];
    const addedRows = [];
    let hasErrors = false;
    let shouldScrollToTop = false;

    // Ensure that all validations were finished

    if (asyncValidateRow) {
      const pendingValidations = [];
      gridApi.forEachNode(rowNode => {
        const { meta } = rowNode.data;
        pendingValidations.push(meta.pendingValidation);
      });
      setArePendingValidations(true);
      await Promise.all(pendingValidations);
      setArePendingValidations(false);
    }

    gridApi.forEachNode(rowNode => {
      const { meta, ...rest } = rowNode.data;
      const { isUpdated, isAdded, validationMessages } = meta;
      if (Object.keys(validationMessages?.errors ?? {}).length > 0) {
        hasErrors = true;
        const newNodeData = {
          ...rowNode.data,
          meta: {
            ...meta,
            forceShowErrors: true,
            sortToTop: shouldBeSortedToTop(meta),
          },
        };
        shouldScrollToTop = shouldScrollToTop || newNodeData.meta.sortToTop;
        rowNode.setData(newNodeData);
        refreshRows({
          refreshClientSideRowModel: true,
        });
      }
      if (isAdded) {
        addedRows.push(includeMetaWhenSaving ? rowNode.data : rest);
        return;
      }
      if (isUpdated) {
        updatedRows.push(includeMetaWhenSaving ? rowNode.data : rest);
        return;
      }
      return;
    });
    if (shouldScrollToTop) {
      gridApi.ensureIndexVisible(0, 'top');
    }
    if (!hasErrors) {
      setEditedRowId(null);
    }
    onSave({
      updatedRows,
      addedRows,
      hasErrors,
      reset,
      findRowIndexBy,
      clearRangeSelection,
    });
  }, [
    gridApi,
    onSave,
    reset,
    refreshRows,
    findRowIndexBy,
    clearRangeSelection,
    includeMetaWhenSaving,
    asyncValidateRow,
    setEditedRowId,
  ]);


  const cancel = useCallback(() => {
    let hasChanges = false;
    gridApi.forEachNode(rowNode => {
      const { isUpdated, isAdded } = rowNode.data.meta;
      if (isUpdated || isAdded) {
        hasChanges = true;
      }
      return;
    });
    onCancel(hasChanges, reset);
    setEditedRowId(null);
  }, [gridApi, onCancel, reset, setEditedRowId]);


  const handleAsyncValidationResult = useCallback((rowNodeId, pendingValidation) => async validationResult => {
    let rowNode = gridApi.getRowNode(rowNodeId);

    if (!rowNode) {
      // Row node not found, skip validation update
      return;
    }

    // Check if the grid is still valid/mounted
    if (!gridApi || !rowNode.data) {
      return;
    }

    const isEditingCells = gridApi.getEditingCells().some(({ rowIndex }) => rowIndex === rowNode.rowIndex);

    if (isEditingCells) {
      await new Promise(resolve => {
        const onCellEditingStopped = () => {
          gridApi.removeEventListener('cellEditingStopped', onCellEditingStopped);
          resolve();
        };
        gridApi.addEventListener('cellEditingStopped', onCellEditingStopped);
      });
      rowNode = gridApi.getRowNode(rowNodeId);

      if (!rowNode || !rowNode.data) {
        // Row node still not found after waiting, skip
        return;
      }
    }

    if (!isCurrentValidation(rowNode, pendingValidation)) {
      return;
    }

    const validationMessages = mergeValidationMessages(rowNode.data.meta.originalValidationMessages, validationResult);
    setDataHasErrors(Object.keys(validationMessages?.errors ?? {}).length > 0);

    // Additional check before setting data to prevent memory leaks
    if (!gridApi || !rowNode.data) {
      return;
    }

    // Use node.setData instead of applyTransaction to avoid ID lookup issues
    const updatedData = {
      ...rowNode.data,
      meta: {
        ...rowNode.data.meta,
        validationMessages,
        lastAsyncValidationMessages: validationResult,
      },
    };

    try {
      rowNode.setData(updatedData);
      refreshRows({
        rowNodes: [rowNode],
      });
    } catch (error) {
      // Failed to update validation result - silently continue to prevent console warnings
    }
  }, [gridApi, refreshRows]);

  const setRowData = useCallback(
    async ({ newValue, node, colDef, field, batchUpdateData, batchUpdateChangeTouched = true }) => {
      const newRow = {
        ...node.data,
        [field ? field : colDef.field]: newValue,
        ...batchUpdateData ? batchUpdateData : {},
      };

      const touched = {
        ...node.data.meta.touched,
        [colDef.field]: true,
      };

      if (batchUpdateData && batchUpdateChangeTouched) {
        Object.keys(batchUpdateData).forEach(field => touched[field] = true);
      }

      const data = [];
      gridApi.forEachNode(rowNode => {
        data.push(rowNode.data);
      });

      const validationMeta = {
        validationMessages: validateRow(newRow, context, data),
      };

      setDataHasErrors(Object.keys(validationMeta.validationMessages?.errors ?? {}).length > 0);

      if (asyncValidateRow) {
        validationMeta.originalValidationMessages = validationMeta.validationMessages;
        validationMeta.validationMessages = mergeValidationMessages(
          validationMeta.validationMessages,
          newRow.meta.lastAsyncValidationMessages
        );
        const pendingValidation = asyncValidateRow(newRow, context, data);
        validationMeta.pendingValidation = pendingValidation;
        validationMeta.pendingValidation.then(handleAsyncValidationResult(node.id, pendingValidation));
      }

      const preservedMeta = {
        ...node.data.meta,
        ...newRow.meta,
        ...validationMeta,
        touched,
      };

      const isAddedRow = node.data.meta.isAdded;
      preservedMeta.isUpdated = !isAddedRow;

      const updatedRowData = {
        ...newRow,
        meta: preservedMeta,
      };

      if (idField && node.data[idField] !== null) {
        updatedRowData[idField] = node.data[idField];
      }

      node.setData(updatedRowData);

      refreshRows({
        rowNodes: [node],
      });

      if (isAddedRow) {
        updateCustomFiltersWithRow(newRow);
        applyFilters();
      }

      return true;
    }, [
      gridApi,
      validateRow,
      asyncValidateRow,
      context,
      refreshRows,
      updateCustomFiltersWithRow,
      handleAsyncValidationResult,
      applyFilters,
      idField,
    ]);

  const addRow = useCallback(additionalData => {
    const newRow = createRow(context, additionalData);

    const data = [];
    const usedIds = new Set();
    gridApi.forEachNode(rowNode => {
      data.push(rowNode.data);
      // Collect existing IDs to avoid duplicates
      const existingId = getRowUniqueId ? getRowUniqueId(rowNode.data) : rowNode.data[idField];
      if (existingId) {
        usedIds.add(existingId);
      }
      if (rowNode.data.meta?.tempId) {
        usedIds.add(rowNode.data.meta.tempId);
      }
      if (rowNode.data.meta?.fallbackId) {
        usedIds.add(rowNode.data.meta.fallbackId);
      }
    });

    // Generate unique temp ID for new row
    let tempId = `temp-${data.length}`;
    let counter = 0;
    while (usedIds.has(tempId)) {
      tempId = `temp-${data.length}-${counter}`;
      counter++;
    }

    const newRowWithMetadata = {
      ...newRow,
      meta: {
        ...newRow.meta,
        isAdded: true,
        tempId,
        sortToTop: shouldBeSortedToTop({ isAdded: true }),
      },
    };

    newRowWithMetadata.meta.validationMessages = validateRow(newRowWithMetadata, context, data);

    const { add } = gridApi.applyTransaction({
      add: [newRowWithMetadata],
      addIndex: 0,
    });

    if (asyncValidateRow) {
      newRowWithMetadata.meta.lastAsyncValidationMessages = {};
      const pendingValidation = asyncValidateRow(newRowWithMetadata, context, data);
      newRowWithMetadata.meta.pendingValidation = pendingValidation;
      newRowWithMetadata.meta.pendingValidation.then(handleAsyncValidationResult(add[0].id, pendingValidation));
    }

    addBlanksToFilters();
    filtersRef.current.custom = {};
    rebuildReactTooltip();

    setTimeout(() => {
      gridApi.forEachNodeAfterFilterAndSort((node, index) => {
        if (node.data?.meta?.isAdded) {
          gridApi.ensureIndexVisible(index, 'top');
          gridApi.setFocusedCell(index, 'companyCode');
          return false;
        }
      });
    }, 100);
  }, [gridApi, createRow, context, validateRow, addBlanksToFilters, asyncValidateRow,
    handleAsyncValidationResult, getRowUniqueId, idField]);

  const stopEditing = useCallback(() => {
    gridApi.stopEditing();
    setEditedRowId(null);
  }, [gridApi]);

  const setEditedRow = useCallback(data => {
    setEditedRowId(getRowUniqueId ? getRowUniqueId(data) : data[idField]);
  }, [setEditedRowId, idField, getRowUniqueId]);

  const postSort = rowNodes => {
    function insert(toIndex, fromIndex) {
      rowNodes.splice(toIndex, 0, rowNodes.splice(fromIndex, 1)[0]);
    }
    let insertPosition = 0;

    for (let index = 0; index < rowNodes.length; index++) {
      const node = rowNodes[index];
      const { sortToTop } = node.data.meta ? node.data.meta : {};
      if (sortToTop) {
        insert(insertPosition, index);
        insertPosition++;
      }
    }
  };

  const onSortChanged = useCallback(() => {
    gridApi.forEachNode(rowNode => {
      const { sortToTop } = rowNode.data.meta ? rowNode.data.meta : {};
      if (sortToTop) {
        rowNode.setData({
          ...rowNode.data,
          meta: {
            ...rowNode.data.meta,
            sortToTop: shouldBeSortedToTop(rowNode.data.meta),
          },
        });
      }
    });
  }, [gridApi]);

  const onModelUpdated = useCallback(() => {
    rebuildReactTooltip();
  }, []);

  const onSelectionChanged = useCallback(() => {
    onSelectionChangedCallback(gridApi);
    onSelect?.(gridApi);
  }, [gridApi, onSelect]);

  return {
    onGridReady,
    reset,
    save,
    cancel,
    setRowData,
    stopEditing,
    addRow,
    getRowNodeId,
    processCellFromClipboard,
    editedRowId,
    setEditedRow,
    postSort,
    onSortChanged,
    onFilterChanged,
    onColumnVisible,
    findRowIndexBy,
    clearRangeSelection,
    onColumnEverythingChanged,
    onModelUpdated,
    arePendingValidations,
    onSelectionChanged,
    gridApi,
    dataHasErrors,
  };
};
